/**
 * Temporary solution: builds a plain-text note containing commercial borrower data
 * (borrower groups, entity assets, company income, property ownership) for storage
 * as an application note via NoteSubType.ApplicationCommercialBorrower.
 *
 * This exists because the BE/DB does not yet have tables for Company, Trust,
 * BorrowerGroup, Director/Trustee roles, or Entity Assets. Once native support
 * is added, this file and the commercialBorrowerData DTO field should be removed.
 */
import { flatMap } from 'lodash-es';
import {
  BorrowerType,
  BorrowerTypeOptions,
  BorrowerTypeSelectionOptions,
  TrusteeTypeOptions,
  TrustStructureOptions,
} from '@/helpers/const';
import { getBorrowerDisplayName, getPropertyOwnershipHeader } from '@/helpers/builder';
import type { Address } from '@/types/Address';
import type { BorrowerGroup, BorrowerRoleEnum } from '@/types/BorrowerGroup';
import type { LoanApplication } from '@/types/LoanApplication';
import type { CompanyIncome } from '@/types/Income';
import type { ApplicantAsset, DescriptionAsset, MotorVehicleAsset, PropertyAsset } from '@/types/Asset';
import type { PropertyOwnership, PropertyOwnershipType } from '@/types/PropertyOwnership';

const HTML_ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

const escapeHtml = (value: string): string => value.replace(/[&<>"']/g, (c) => HTML_ESCAPE_MAP[c] ?? c);

const RoleLabels: Record<BorrowerRoleEnum, string> = {
  individual: 'Individual Borrower',
  director: 'Director',
  trustee: 'Trustee',
  partner: 'Partner',
};

const formatAddress = (address?: Address): string => {
  if (!address) return 'N/A';
  const parts = [address.addressLine, address.addressLine2, address.locality, address.state, address.postcode]
    .filter((p): p is string => Boolean(p))
    .map(escapeHtml);
  return parts.length > 0 ? parts.join(', ') : 'N/A';
};

const formatCurrency = (amount?: number): string => (amount != null ? `$${amount.toLocaleString('en-AU')}` : 'N/A');

const buildCompanyDetails = (cd: NonNullable<BorrowerGroup['companyDetails']>): string[] => {
  const lines: string[] = [];
  if (cd.companyName) lines.push(`  Company Name: ${escapeHtml(cd.companyName)}`);
  if (cd.abnAcnNumber) lines.push(`  ABN/ACN: ${escapeHtml(cd.abnAcnNumber)}`);
  if (cd.isTrading != null) lines.push(`  Trading: ${cd.isTrading ? 'Yes' : 'No'}`);
  if (cd.registeredAddress) lines.push(`  Registered Address: ${formatAddress(cd.registeredAddress)}`);
  return lines;
};

const buildBorrowerGroupSection = (group: BorrowerGroup, data: LoanApplication): string => {
  const displayName = getBorrowerDisplayName(group, data.applicants);
  const typeLabel = BorrowerTypeOptions[group.type] ?? group.type;
  const lines: string[] = [`<strong>[${typeLabel}] ${escapeHtml(displayName)}</strong>`];

  if (group.type === BorrowerType.COMPANY && group.companyDetails) {
    lines.push(...buildCompanyDetails(group.companyDetails));
  }

  if (group.type === BorrowerType.TRUST && group.trustDetails) {
    const td = group.trustDetails;
    if (td.trustName) lines.push(`  Trust Name: ${escapeHtml(td.trustName)}`);
    if (td.trusteeType) lines.push(`  Trustee Type: ${TrusteeTypeOptions[td.trusteeType] ?? td.trusteeType}`);
    if (td.trustStructure)
      lines.push(`  Trust Structure: ${TrustStructureOptions[td.trustStructure] ?? td.trustStructure}`);
    if (td.companyDetails) {
      lines.push('  Trustee Company:');
      lines.push(...buildCompanyDetails(td.companyDetails).map((l) => `  ${l}`));
    }
  }

  if (group.applicants?.length) {
    lines.push('  People:');
    for (const ba of group.applicants) {
      const applicant = data.applicants.find((a) => a.id === ba.applicantId);
      if (applicant) {
        const name = escapeHtml(`${applicant.firstName ?? ''} ${applicant.lastName ?? ''}`.trim());
        lines.push(`  - ${name} (${RoleLabels[ba.role] ?? ba.role})`);
      }
    }
  }

  return lines.join('\n');
};

const buildCompanyIncomeSection = (data: LoanApplication): string => {
  const groups = data.borrowerGroups ?? [];
  const entries = groups
    .filter((g) => g.type !== BorrowerType.INDIVIDUAL)
    .map((group) => {
      const firstApplicantId = group.applicants?.[0]?.applicantId;
      const companyIncome: CompanyIncome | null | undefined = firstApplicantId
        ? data.financials[firstApplicantId]?.companyIncome
        : undefined;

      if (!companyIncome?.netProfit?.amount) return null;

      const displayName = escapeHtml(getBorrowerDisplayName(group, data.applicants));
      const amount = formatCurrency(companyIncome.netProfit.amount);
      const frequency = companyIncome.netProfit.frequency ?? '';
      return `  ${displayName}: ${amount} (${frequency})`;
    })
    .filter(Boolean);

  if (entries.length === 0) return '';
  return `<strong>COMPANY/TRUST INCOME (NET PROFIT)</strong>\n${entries.join('\n')}`;
};

const isPropertyAsset = (a: ApplicantAsset): a is PropertyAsset => a.type === 'property';
const isMotorVehicleAsset = (a: ApplicantAsset): a is MotorVehicleAsset => a.type === 'motor_vehicles';
const isDescriptionAsset = (a: ApplicantAsset): a is DescriptionAsset =>
  a.type === 'machinery' || a.type === 'equipment';

const formatResidualDebtSuffix = (asset: {
  useAssetForResidualDebt?: boolean;
  amountTowardsResidualDebt?: number;
}): string =>
  asset.useAssetForResidualDebt && asset.amountTowardsResidualDebt
    ? ` (Residual debt: ${formatCurrency(asset.amountTowardsResidualDebt)})`
    : '';

const formatAssetLine = (asset: ApplicantAsset): string => {
  if (isPropertyAsset(asset)) {
    return `  - Property: ${formatAddress(asset.address)} - Value: ${formatCurrency(
      asset.propertyValue,
    )}, Owing: ${formatCurrency(asset.amountOwing)}`;
  }
  if (isMotorVehicleAsset(asset)) {
    return `  - Motor vehicle: ${escapeHtml(asset.make)} ${escapeHtml(asset.model)} (${
      asset.yearBought
    }) - ${formatCurrency(asset.value)}${formatResidualDebtSuffix(asset)}`;
  }
  if (isDescriptionAsset(asset)) {
    return `  - ${asset.type}: ${formatCurrency(asset.value)} - ${escapeHtml(
      asset.description,
    )}${formatResidualDebtSuffix(asset)}`;
  }
  return `  - ${asset.type}: ${formatCurrency(asset.value)} - ${escapeHtml(
    asset.institution,
  )}${formatResidualDebtSuffix(asset)}`;
};

const buildEntityAssetsSection = (data: LoanApplication): string => {
  const entityAssets = data.entityAssets;
  if (!entityAssets || Object.keys(entityAssets).length === 0) return '';

  const groups = data.borrowerGroups ?? [];
  const sections: string[] = [];

  for (const [groupId, assets] of Object.entries(entityAssets)) {
    if (!assets?.length) continue;

    const group = groups.find((g) => g.id === groupId);
    const displayName = escapeHtml(group ? getBorrowerDisplayName(group, data.applicants) : groupId);

    const assetLines = assets.map(formatAssetLine);

    sections.push(`  ${displayName}:\n${assetLines.join('\n')}`);
  }

  if (sections.length === 0) return '';
  return `<strong>ENTITY ASSETS</strong>\n${sections.join('\n\n')}`;
};

const resolveOwnerName = (ownerId: string, data: LoanApplication): string => {
  const groups = data.borrowerGroups ?? [];
  const group = groups.find((g) => g.id === ownerId);
  if (group) return getBorrowerDisplayName(group, data.applicants);

  const applicant = data.applicants.find((a) => a.id === ownerId);
  if (applicant) return escapeHtml(`${applicant.firstName ?? ''} ${applicant.lastName ?? ''}`.trim());

  return ownerId;
};

const buildOwnershipSection = (data: LoanApplication): string => {
  if (!data.borrowerGroups?.length) return '';

  type PropertyInfo = { type: PropertyOwnershipType; address?: Address; id: string };

  const allProperties: PropertyInfo[] = [
    ...data.incomingProperties.map((p) => ({ type: 'incoming' as const, address: p.address, id: p.id })),
    ...data.outgoingProperties.map((p) => ({ type: 'outgoing' as const, address: p.address, id: p.id })),
    ...(data.supportingProperties ?? []).map((p) => ({
      type: 'supporting' as const,
      address: p.address,
      id: p.id,
    })),
  ];

  const lines: string[] = [];
  for (const prop of allProperties) {
    const po: PropertyOwnership | undefined = data.propertiesOwnership.find((o) => o.id === prop.id);
    if (!po) continue;

    const header = escapeHtml(getPropertyOwnershipHeader(prop.type, prop.address));
    lines.push(`  ${header}:`);

    for (const [ownerId, share] of Object.entries(po.share)) {
      lines.push(`  - ${resolveOwnerName(ownerId, data)}: ${share}%`);
    }
  }

  if (lines.length === 0) return '';
  return `<strong>PROPERTY OWNERSHIP</strong>\n${lines.join('\n')}`;
};

interface AssetOwnershipEntry {
  ownerName: string;
  address?: Address;
  share: Record<string, number>;
}

const collectAssetPropertyOwnership = (data: LoanApplication): AssetOwnershipEntry[] => {
  const groups = data.borrowerGroups ?? [];

  const individualAssets = flatMap(data.financials, (fin) => fin.assets)
    .filter(isPropertyAsset)
    .filter((a) => a.propertyOwnership?.share);

  const entityAssets = flatMap(data.entityAssets ?? {}, (assets, groupId) =>
    assets
      .filter(isPropertyAsset)
      .filter((a) => a.propertyOwnership?.share)
      .map((a) => ({ ...a, _entityGroupId: groupId })),
  );

  const toEntry = (asset: PropertyAsset, ownerLabel?: string): AssetOwnershipEntry => ({
    ownerName: ownerLabel ?? 'Unknown',
    address: asset.address,
    share: asset.propertyOwnership?.share ?? {},
  });

  const individualEntries = individualAssets.map((asset) => {
    const applicant = data.applicants.find((a) => data.financials[a.id]?.assets.some((fa) => fa === asset));
    const name = applicant ? `${applicant.firstName ?? ''} ${applicant.lastName ?? ''}`.trim() : 'Unknown';
    return toEntry(asset, name);
  });

  const entityEntries = entityAssets.map((asset) => {
    const group = groups.find((g) => g.id === asset._entityGroupId);
    const name = group ? getBorrowerDisplayName(group, data.applicants) : asset._entityGroupId;
    return toEntry(asset, name);
  });

  return [...entityEntries, ...individualEntries];
};

const buildAssetPropertyOwnershipSection = (data: LoanApplication): string => {
  if (!data.borrowerGroups?.length) return '';

  const entries = collectAssetPropertyOwnership(data);
  if (entries.length === 0) return '';

  const lines = entries.flatMap((entry) => {
    const header = escapeHtml(getPropertyOwnershipHeader('asset', entry.address));
    return [
      `  ${escapeHtml(entry.ownerName)} > ${header}:`,
      ...Object.entries(entry.share).map(([ownerId, share]) => `  - ${resolveOwnerName(ownerId, data)}: ${share}%`),
    ];
  });

  return `<strong>ASSET PROPERTY OWNERSHIP</strong>\n${lines.join('\n')}`;
};

export const hasNonIndividualBorrowers = (data: LoanApplication): boolean =>
  (data.borrowerGroups ?? []).some((g) => g.type !== BorrowerType.INDIVIDUAL);

export const buildCommercialBorrowerNote = (data: LoanApplication): string => {
  const sections: string[] = ['<strong>COMMERCIAL BORROWER DETAILS</strong>'];

  if (data.borrowerTypeSelection) {
    const label = BorrowerTypeSelectionOptions[data.borrowerTypeSelection] ?? data.borrowerTypeSelection;
    sections.push(`Borrower Type: ${escapeHtml(label)}`);
  }
  if (data.assessmentType) sections.push(`Assessment Type: ${data.assessmentType}`);
  if (data.netSurplus != null) sections.push(`Net Surplus: ${formatCurrency(data.netSurplus)}`);

  const groupLines = (data.borrowerGroups ?? []).map((group) => buildBorrowerGroupSection(group, data));
  if (groupLines.length > 0) {
    sections.push(`\n<strong>BORROWER GROUPS</strong>\n\n${groupLines.join('\n\n')}`);
  }

  const companyIncome = buildCompanyIncomeSection(data);
  if (companyIncome) sections.push(`\n${companyIncome}`);

  const entityAssets = buildEntityAssetsSection(data);
  if (entityAssets) sections.push(`\n${entityAssets}`);

  const ownership = buildOwnershipSection(data);
  if (ownership) sections.push(`\n${ownership}`);

  const assetOwnership = buildAssetPropertyOwnershipSection(data);
  if (assetOwnership) sections.push(`\n${assetOwnership}`);

  const body = sections.join('\n');
  return `<div style="white-space: pre-wrap;">${body}</div>`;
};
